import type {GetServerSideProps, InferGetServerSidePropsType, NextPage} from 'next';
import {GetServerSidePropsContext} from 'next/types';
import Head from 'next/head';
import {MetaTags} from '@helpers/meta-tags.helper';
import {getArticles, getPopularArticles} from '@services/blog.service';
import MainLayout from '@layouts/main-layout/main-layout';
import BlogPage from '@components/blog/blog-page/blog-page';
import {BLOG_TOPICS, DEFAULT_BLOG_TOPIC} from '@models/blog.topics';
import { BLOG_TOPIC_METADATA } from '@libs/utils/article.utils';
import { useRouter } from 'next/router';
import { getMatchingRedirect } from '@services/redirect.service';

const LIMIT = 8;

const Blog: NextPage = ({articles, popular, topic, total, page}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const { asPath } = useRouter();
    const metadata = BLOG_TOPIC_METADATA[topic] ?? BLOG_TOPIC_METADATA.default;
    return (
        <>
            <Head>{
                MetaTags({
                    h1: metadata.h1,
                    title: metadata.title(page),
                    description: metadata.description(page),
                    imageUrl: '/blog-images/blog-meta-img.png',
                    pageUrl: asPath,
                })
            }
                <link rel="icon" href="/favicon.ico"/>
            </Head>
            <MainLayout>
                <BlogPage h1={metadata.h1} articles={articles} popular={popular} topic={topic} total={total} page={page}/>
            </MainLayout>
        </>

    )
}

export default Blog;

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
    let topic = DEFAULT_BLOG_TOPIC.slug;
    let pageRequest = 0;

    const redirectUrl = await getMatchingRedirect(context.resolvedUrl);

    if (redirectUrl) {
        return {
        redirect: {
            destination: redirectUrl,
            permanent: true,
        },
        };
    }

    if (typeof context.query.topic === 'string') {
        const isValid = BLOG_TOPICS.some((t) => t.slug === context.query.topic);
        if (!isValid) {
        return { notFound: true };
        }
        topic = context.query.topic;
    }

    if (context.query.page) {
        pageRequest = Number(context.query.page) - 1;
    }

    const [articles, popular] = await Promise.all([
        getArticles(true, LIMIT * pageRequest, LIMIT, topic),
        getPopularArticles(true),
    ]);

    const currentPageIsBiggerThanLastPage = articles.total < LIMIT * pageRequest;

    if (currentPageIsBiggerThanLastPage) {
           return {
            notFound: true,
        };
    }

    return {
        props: {
            articles: articles.data,
            popular: popular.data,
            topic: topic,
            total: articles.total,
            page: (pageRequest + 1),
        }
    }
}
